/**
 * @file PreAssignModal.js
 * @author Simon Tenedero
 * @created 2025-07-24
 * @lastModified 2025-07-24
 * @description file containing PreAssignModal component
 */

import React, { useState, useEffect } from "react";
import {
  preAssignRole,
  removePreAssignedRole,
  fetchPreAssignedRoles,
} from "../../utils/preAssignUtils";
/**
 * This component is a modal (popup) that allows users to update an existing user group (this is used in the admin panel).
 *
 * @param {Object} props - The component properties
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onRolePreAssigned - Callback function to call when a role is pre-assigned
 *
 * @returns {JSX.Element} The rendered PreAssignModal component
 */

const PreAssignModal = ({
  isOpen,
  onClose,
  rolesData, // default roles
}) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [items, setItems] = useState([]);

  const groupOptions = rolesData.map((role) => role.roleName || "Unknown Role");
  console.log("rolesData", rolesData);

  // on component mount, fetch pre-assigned roles
  useEffect(() => {
    // Fetch pre-assigned roles when the modal opens
    if (isOpen) {
      fetchPreAssignedRoles()
        .then((fetchedItems) => {
          setItems(fetchedItems);
        })
        .catch((error) => {
          console.error("Error fetching pre-assigned roles:", error);
        });
    }
  }, [isOpen]);

  // Handle adding new item
  const handleAddItem = async (e) => {
    e.preventDefault(); //prevent event bubbling

    // Check if both input and role are selected
    if (inputValue.trim() && selectedRole) {
      const newItem = {
        email: inputValue.trim().toLowerCase(), // ensure email is lowercase
        roleName: selectedRole,
        roleId: rolesData.find((role) => role.roleName === selectedRole)?.id,
        id: selectedRole + inputValue.trim(), // Unique ID based on role and email (when we refetch on next component mount, this will be replaced with the ID from the database)
      };

      // Check for duplicates based on email and role combination
      const isDuplicate = items.some(
        (item) =>
          item.email === newItem.email && item.roleName === newItem.roleName,
      );

      if (!isDuplicate) {
        setItems([...items, newItem]);
        setInputValue("");
        setSelectedRole("");

        try {
          await preAssignRole(newItem);
        } catch (error) {
          console.error("Error pre-assigning role:", error);
        }
      }
    }
  };

  // Handle removing item
  const handleRemoveItem = async (emailToRemove) => {
    try {
      await removePreAssignedRole(emailToRemove);
      setItems(items.filter((item) => item.email !== emailToRemove));
    } catch (error) {
      console.error("Error removing pre-assigned role:", error);
    }
  };

  // Handle role selection
  const handleRoleChange = (e) => {
    setSelectedRole(e.target.value);
  };

  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Handle modal close
  const handleClose = () => {
    setInputValue("");
    setSelectedRole("");
    setItems([]);
    onClose();
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-base-200 rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-scroll">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-2xl font-bold text-base-content">
                Pre-Assign Email
              </h2>
              <p className="text-base-content/70">
                Assign emails to groups. Users auto-join their assigned group
                upon registration.
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="btn btn-ghost btn-sm btn-circle"
          >
            <span className="text-lg">×</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Input Section */}
          <div className="space-y-3">
            {/* Text Input */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">
                  Email<span className="text-red-500">*</span>
                </span>
              </label>
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder="e.g. user@example.com"
                className="input input-bordered w-full"
              />
            </div>

            {/* Attribute Dropdown */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">
                  Group<span className="text-red-500">*</span>
                </span>
              </label>
              <select
                value={selectedRole}
                onChange={handleRoleChange}
                className="select select-bordered w-full"
              >
                <option value=""> Select Group </option>
                {groupOptions.map((title, index) => (
                  <option key={index} value={title}>
                    {title}
                  </option>
                ))}
              </select>
            </div>

            {/* Add Button */}
            <button
              onClick={handleAddItem}
              disabled={!inputValue.trim() || !selectedRole}
              className="btn btn-primary w-full"
            >
              assign email
            </button>
          </div>

          {/* Items List */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Assigned Emails</span>
            </label>

            <div className="bg-base-100 rounded-lg p-4 min-h-[120px] max-h-[250px] overflow-y-scroll border border-base-300">
              {items.length === 0 ? (
                <p className="text-base-content/70 text-center py-4">
                  No items added yet
                </p>
              ) : (
                <div className="space-y-2">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-base-200 rounded-lg hover:bg-base-300 transition-colors"
                    >
                      <div className="flex-1 mr-2">
                        <div className="text-base-content font-medium">
                          {item.email}
                        </div>
                        <div className="text-sm text-base-content/70">
                          <span className="inline-block bg-primary/20 text-primary px-2 py-1 rounded-full text-xs">
                            {item.roleName}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(item.email)}
                        className="btn btn-ghost btn-sm btn-circle text-error hover:bg-error/20"
                        title="Remove item"
                      >
                        <span className="text-lg">×</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Item count */}
            {items.length > 0 && (
              <label className="label">
                <span className="label-text-alt">
                  {items.length} item{items.length !== 1 ? "s" : ""} added
                </span>
              </label>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-base-300">
          <button onClick={handleClose} className="btn btn-ghost">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreAssignModal;
